import { type AudioCourseCMS } from "@repo/common/src/types/firestore/cms/audioCourse";
import { CourseCategories } from "@repo/common/src/types/firestore/cms/baseCourse";
import { CMSCollections } from "@repo/common/src/types/firestore/collections";
import { StoragePaths } from "@repo/common/src/types/storage/storagePaths";
import { doc, getDoc, getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import {
  type EntityOnSaveProps,
  type EntityReference,
  buildCollection,
  buildEntityCallbacks,
  buildProperty,
} from "@firecms/core";

import { FrontendError, FrontendErrorCodesCms, readCmsTimestampMillis } from "@repo/common";
import { standardPermissions } from "../../utils/constants";
import { assertLessonDurationWithinCap } from "../../utils/assertLessonDurationWithinCap";
import { fileOperationsAudioCourse } from "../../utils/fileOperationsAudioCourse";
import { generateUniqueSlug } from "../../utils/generateUniqueSlug";
import { getAudioDuration } from "../../utils/getAudioDuration";
import { loadingOverlay } from "../../utils/loadOverlay";
import { Groups } from "../types";

type AudioCourseCMSModel = Omit<AudioCourseCMS, "author"> & { author: EntityReference };
type AudioCourseCMSFormModel = Omit<AudioCourseCMSModel, "date" | "created_on" | "updated_on"> & {
  date: Date;
  created_on: Date;
  updated_on: Date;
};

/** Fills the FireCMS date picker from stored millis or a leftover date field. */
const hydrateCmsLessonPublishDate = <T extends { publish_date?: unknown; publish_date_milis?: unknown }>(
  lesson: T,
): T => {
  if (lesson.publish_date instanceof Date) {
    return lesson;
  }

  const publishMs = readCmsTimestampMillis(lesson.publish_date_milis) ?? readCmsTimestampMillis(lesson.publish_date);

  if (publishMs === null) {
    return lesson;
  }

  return { ...lesson, publish_date: new Date(publishMs) };
};

const callbacks = buildEntityCallbacks({
  onFetch: ({ entity }) => {
    if (!entity.values.files) {
      return entity;
    }

    return {
      ...entity,
      values: {
        ...entity.values,
        files: entity.values.files.map((file) => hydrateCmsLessonPublishDate(file)),
      },
    };
  },
  onPreSave: async (props: EntityOnSaveProps<AudioCourseCMSFormModel>) => {
    const { values, entityId } = props;

    loadingOverlay.show();

    if (values.name) {
      const baseSlug = values.slug || values.name;
      values.slug = await generateUniqueSlug(getFirestore(), CMSCollections.AudioCourses, baseSlug, entityId);
    }

    if (!values.files) {
      return values;
    }

    const savedFileValues = values.files;

    const storage = getStorage();
    const db = getFirestore();

    // Fetch the original document to compare
    const savedDataInStore = entityId
      ? await getDoc(doc(db, CMSCollections.AudioCourses, entityId)).then(
          (docSnap) => docSnap.data() as AudioCourseCMS | undefined,
        )
      : undefined;

    // Phase 1: validate every lesson duration before any storage copy/delete.
    await Promise.all(
      values.files.map(async (file: AudioCourseCMS["files"][number]) => {
        const duration = await getAudioDuration(file.file, storage);
        assertLessonDurationWithinCap(duration);
      }),
    );

    // Phase 2: move files only after the whole course validated.
    const updatedFiles = await Promise.all(
      values.files.map(async (file: AudioCourseCMS["files"][number], index: number) => {
        const savedValuesOfFileInDB = savedDataInStore?.files[index];
        const valuesOnSave = savedFileValues[index];

        try {
          return await fileOperationsAudioCourse({
            file,
            savedValuesOfFileInDB,
            valuesOnSave,
            storage,
            isPremium: valuesOnSave.premium ?? false,
          });
        } catch (error: unknown) {
          if (error instanceof FrontendError) {
            throw error;
          }
          throw new FrontendError(
            "Audio konnte nicht verarbeitet werden. Bitte Datei prüfen und erneut versuchen.",
            FrontendErrorCodesCms.AudioProcessingFailed,
            "danger",
          );
        }
      }),
    );

    return { ...values, files: updatedFiles };
  },
  onSaveSuccess: async () => {
    loadingOverlay.hide();
  },
  onSaveFailure: async () => {
    loadingOverlay.hide();
  },
});

// Creates an Object out of the CourseCategories Enums
const categories = Object.entries(CourseCategories).reduce((obj: Record<string, CourseCategories>, [key, value]) => {
  obj[key] = value;
  return obj;
}, {});

export const audioCourses = buildCollection<AudioCourseCMSFormModel>({
  id: CMSCollections.AudioCourses,
  name: "Audio Kurse",
  singularName: "Audio Kurs",
  path: CMSCollections.AudioCourses,
  icon: "Audiotrack",
  group: Groups.Courses,
  permissions: () => {
    return standardPermissions;
  },
  subcollections: [],
  callbacks,
  properties: {
    published: {
      name: "Veröffentlicht",
      dataType: "boolean",
      description: "Soll der Kurs auf der App sichtbar sein?",
    },
    premium: {
      name: "Premium",
      dataType: "boolean",
      description: "Soll der Kurs nur für Premium Nutzer sichtbar sein?",
    },
    isSequential: ({ values }) => ({
      name: "Sequentiell",
      dataType: "boolean",
      defaultValue: true,
      description: "Sollen die Lektionen nach einander abgespielt werden?",
      disabled:
        (values.blockNextLessonInDays ?? 0) > 0
          ? {
              clearOnDisabled: true,
              disabledMessage: "Sequentiell muss deaktiviert sein, wenn eine Tagessperre gesetzt ist.",
            }
          : false,
    }),
    blockNextLessonInDays: {
      name: "Sperre nächste Lektion (Tage)",
      dataType: "number",
      defaultValue: 0,
      description:
        "Anzahl der Tage, die vergehen müssen, bevor die nächste Lektion freigeschaltet wird. 0 = keine Sperre, 1 = eine Lektion pro Tag, etc.",
      validation: {
        min: 0,
      },
    },
    name: {
      name: "Name",
      validation: { required: true },
      dataType: "string",
    },
    slug: {
      name: "URL-freundlicher Slug. Wird automatisch generiert, wenn leer.",
      dataType: "string",
    },
    related_course: {
      dataType: "array",
      name: "Ähnliche Kurse",
      of: {
        dataType: "reference",
        path: "audioCourses",
      },
    },
    main_image: buildProperty({
      name: "Kursbild",
      description: "Bild des Kurses. Bitte in 1024x1024 hochladen.",
      dataType: "string",
      validation: { required: true },
      storage: {
        storagePath: StoragePaths.AudioCoursesImages,
        acceptedFiles: ["image/*"],
        maxSize: 1 * 1024 * 1024, // 1 MB in bytes
        imageCompression: {
          maxHeight: 1024,
          maxWidth: 1024,
        },
      },
    }),
    description: {
      name: "Beschreibung",
      multiline: true,
      dataType: "string",
      validation: { required: true },
      columnWidth: 300,
    },
    categories: {
      name: "Kategorien",
      validation: { required: true },
      dataType: "array",
      of: {
        dataType: "string",
        enumValues: categories,
      },
    },
    author: {
      dataType: "reference",
      name: "Autor",
      validation: { required: true },
      path: CMSCollections.Authors,
    },
    date: {
      name: "Veröffentlichungsdatum",
      validation: { required: true },
      dataType: "date",
    },
    created_on: buildProperty({
      name: "Erstellungsdatum",
      dataType: "date",
      autoValue: "on_create",
    }),
    updated_on: buildProperty({
      name: "Aktualisierungsdatum",
      dataType: "date",
      autoValue: "on_update",
    }),
    files: {
      name: "Audio",
      dataType: "array",
      validation: { required: true },
      of: {
        dataType: "map",
        name: "Audio Datei",
        properties: {
          publish_date: {
            name: "Veröffentlichungsdatum",
            description: "Wann soll die Datei veröffentlicht werden? Wenn leer, wird die Datei sofort veröffentlicht.",
            dataType: "date",
          },
          premium: {
            name: "Premium",
            dataType: "boolean",
            defaultValue: false,
            description: "Soll die Datei nur für Premium Nutzer nutzbar sein?",
          },
          name: {
            name: "Name",
            dataType: "string",
            validation: { required: true },
          },
          closing: {
            name: "Schlusstext",
            multiline: true,
            dataType: "string",
            validation: { required: true },
            columnWidth: 300,
          },
          file: {
            name: "Audio Datei",
            dataType: "string",
            validation: { required: true },
            storage: {
              storagePath: StoragePaths.AudioCoursesFree,
              acceptedFiles: ["audio/*"],
              maxSize: 100 * 1024 * 1024, // 100 MB in bytes
            },
          },
          homework_name: {
            name: "Hausaufgaben Name",
            dataType: "string",
          },
          homework_file: {
            name: "Hausaufgaben Datei",
            description: "Bitte als PDF hochladen. Maximal 10 MB.",
            dataType: "string",
            storage: {
              storagePath: StoragePaths.AudioCoursesHomeworkFree,
              acceptedFiles: ["application/pdf"],
              maxSize: 10 * 1024 * 1024, // 10 MB in bytes
            },
          },
        },
      },
    },
  },
});
